import { defineMessages } from 'react-intl';

export const trialRecapModalMessages = defineMessages({
  daysLeft: {
    defaultMessage: '{daysLeft, plural, one {# day} other {# days}} left',
    id: 'Cewn3PxBWH',
    description:
      'Days remaining label above the trial recap heading; pluralised count of days left in the trial',
  },
  titleSoloClinician: {
    defaultMessage: "What you've done on Clinician",
    id: 'E1IjZagRs/',
    description:
      'Heading on the Clinician trial recap modal for a non–team-admin; solo framing. "tierName" is the Clinician plan being trialled',
  },
  titleTeamAdmin: {
    defaultMessage: "Here's what your team's done on {tierName}",
    id: 'gjiDiYancz',
    description:
      'Heading on the trial recap modal for a team admin; team-oriented framing',
  },
  titleSoloPractice: {
    defaultMessage: 'How you’ve been using evidence on Practice',
    id: 'vF6pFAs9iD',
    description:
      'Heading on the Practice trial recap modal for a non–team-admin with meaningful trial usage; solo framing. "tierName" is the Practice plan being trialled',
  },
  descriptionClinicianSolo: {
    defaultMessage:
      "In {daysLeft, plural, one {# day} other {# days}} you'll return to <link>Scribe Plus</link>, (formerly Heidi Pro). No billing changes.",
    id: '1oFQ2p6Nlo',
    description:
      "Subtitle under the trial recap modal heading for a Clinician trial viewed by a non–team-admin; explains which plan the user reverts to when the trial ends. 'Scribe Plus' is wrapped in a link to the plan-changes help article.",
  },
  descriptionClinicianTeamAdmin: {
    defaultMessage:
      'In {daysLeft, plural, one {# day} other {# days}} your team will return to <link>Scribe Plus</link>, (formerly Heidi Pro). No billing changes.',
    id: 'YeqcCZ9MBJ',
    description:
      "Subtitle under the trial recap modal heading for a Clinician trial viewed by a team admin; explains which plan the team reverts to when the trial ends. 'Scribe Plus' is wrapped in a link to the plan-changes help article.",
  },
  descriptionPracticeSolo: {
    defaultMessage:
      "In {daysLeft, plural, one {# day} other {# days}} you'll return to <link>Scribe Team</link>, (formerly called Practice). No billing changes.",
    id: 'G0doRpFz9P',
    description:
      "Subtitle under the trial recap modal heading for a Practice trial viewed by a non–team-admin; explains which plan the user reverts to when the trial ends. 'Scribe Team' is wrapped in a link to the plan-changes help article.",
  },
  descriptionPracticeTeamAdmin: {
    defaultMessage:
      'In {daysLeft, plural, one {# day} other {# days}} your team will return to <link>Scribe Team</link>, (formerly called Practice). No billing changes.',
    id: 'ff0SvPFH/e',
    description:
      "Subtitle under the trial recap modal heading for a Practice trial viewed by a team admin; explains which plan the team reverts to when the trial ends. 'Scribe Team' is wrapped in a link to the plan-changes help article.",
  },
  featureLabelEvidenceAnswers: {
    defaultMessage: 'Evidence answers',
    id: '4EG7lBhzOe',
    description:
      'Label for the evidence answers row in the trial recap modal; maps to assistant-style turns in Evidence chat',
  },
  evidenceAnswersTooltip: {
    defaultMessage:
      'Every clinical answer and document Evidence produced, including summaries, handouts and care plans.',
    id: 'VdPZDkIMpi',
    description:
      'Tooltip next to Evidence answers in the trial recap feature list describing clinical answers and documents Evidence produces',
  },
  evidenceAnswersTooltipAriaLabel: {
    defaultMessage: 'More about Evidence answers',
    id: '8y7Hassvkb',
    description:
      'Accessible label for the info control that shows the Evidence answers tooltip on the trial recap feature list',
  },
  featureLabelAnswersInPatientSessions: {
    defaultMessage: 'Answers in patient sessions',
    id: '0TUFnVj36k',
    description:
      'Label for the session-linked evidence query row in the trial recap modal',
  },
  featureLabelCitationsSourced: {
    defaultMessage: 'Citations sourced',
    id: 'sbvqAt5uxM',
    description:
      'Label for the CPD citations row in the trial recap modal (references used)',
  },
  usageEvidenceAnswers: {
    defaultMessage: 'Used {count}x',
    id: 'SBiVd98nJe',
    description:
      'Usage badge on the Evidence answers row in the trial recap modal, e.g. "Used 34x"',
  },
  usageAnswersInPatientSessions: {
    defaultMessage: '{count, plural, one {# session} other {# sessions}}',
    id: 'DairBkazkV',
    description:
      'Usage badge on the Answers in patient sessions row; count is the number of sessions',
  },
  usageCitationsSourcedCompact: {
    defaultMessage: '{rounded}+',
    id: 'Yb2wGGn91w',
    description:
      'Usage badge for Citations sourced when count exceeds 1000; rounded is the count floored to the nearest hundred, e.g. "1200+"',
  },
  upgradeCtaClinician: {
    defaultMessage: 'Upgrade to Clinician',
    id: 'wNq5Zt3jrh',
    description:
      'Primary CTA on the trial recap modal that takes the user to the upgrade flow; "planName" is the plan being trialled (e.g. Clinician, Practice)',
  },
  upgradeCtaPractice: {
    defaultMessage: 'Keep the new Practice plan',
    id: 'C+V0vGJePA',
    description:
      'Primary CTA on the Practice trial recap modal that takes the user to the upgrade flow; "planName" is the Practice plan being trialled',
  },
  talkToUsKeepPlanCtaClinician: {
    defaultMessage: 'Talk to us to keep Clinician',
    id: '4MZ0oSmpDz',
    description:
      'Primary CTA on the trial recap modal for a team admin when the team is not Stripe-managed; opens contact sales. "tierName" is the plan (e.g. Clinician, Practice)',
  },
  talkToUsKeepPlanCtaPractice: {
    defaultMessage: 'Talk to us to keep the new Practice plan',
    id: 'h6R5P70KyL',
    description:
      'Primary CTA on the trial recap modal for a team admin when the team is not Stripe-managed; opens contact sales. "tierName" is the plan (e.g. Clinician, Practice)',
  },
  remindMeLater: {
    defaultMessage: 'Remind me later',
    id: 'Jk5DpIzQYb',
    description:
      'Secondary CTA on the trial recap modal that dismisses the modal for now',
  },
  clinicianPlanName: {
    defaultMessage: 'Clinician',
    id: 'ppm7JSJe+q',
    description: 'Display name for the Clinician plan in the trial recap modal',
  },
  practicePlanName: {
    defaultMessage: 'Practice',
    id: '5I9F6IpF5t',
    description: 'Display name for the Practice plan in the trial recap modal',
  },
});
